import { html, nothing, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { OutlineCard } from '../../base/outline-card/outline-card';
import { LinkTargetType } from '../../base/outline-link/config';
import '../ghs-icon/ghs-icon';

export type CardVariant = 'default' | 'dark' | 'simple' | 'simple-link';

import componentStyles from './ghs-card.css.lit';
import { LightController } from '../../controllers/light-controller';

/**
 * The Card component.
 * @element ghs-card
 * @slot image - The image slot.
 * @slot eyebrow - The eyebrow slot.
 * @slot heading - The heading slot.
 * @slot subheading - The subheading slot.
 * @slot content - The primary slot to render the card content.
 * @slot cta - The cta slot.
 */
@customElement('ghs-card')
export class GhsCard extends OutlineCard {
  static styles = [componentStyles];

  lightController = new LightController(
    this,
    componentStyles,
    this.tagName.toLowerCase()
  );

  /**
   * Card variant.
   */
  @property() variant: CardVariant = 'default';

  /**
   * Whether or not this card is fully clickable.
   */
  @property({ type: Boolean, attribute: 'is-clickable' })
  isClickable = false;

  /**
   * The icon to use in the subheading slot.
   */
  @property({ type: String, attribute: 'icon-name' })
  iconName = 'calendar-v2';

  /**
   * Optionally provides a target for the isClickable property.
   */
  @property({ type: String, attribute: 'is-clickable-target', reflect: true })
  isClickableTarget: LinkTargetType = '_self';

  /**
   * If TRUE, image will span the full width/height of the card
   * and the text will be overlayed on top of it.
   */
  @property({ type: Boolean, attribute: 'image-bleed', reflect: true })
  imageBleed = false;

  /**
   * Sets the opacity level of the card's overlay
   */
  @property({ type: Number, attribute: 'overlay-opacity', reflect: true })
  overlayOpacity:
    | '0'
    | '10'
    | '15'
    | '20'
    | '25'
    | '30'
    | '35'
    | '40'
    | '45'
    | '50'
    | '55'
    | '60'
    | '65'
    | '70'
    | '75'
    | '80'
    | '85'
    | '90'
    | '100' = '75';

  /**
   * If TRUE, the image will be positioned on the sideway.
   */
  @property({ type: Boolean, attribute: 'image-sideway', reflect: true })
  imageSideway = false;

  /**
   * If TRUE, the card will show a background color instead of the image.
   */
  @property({ type: Boolean, attribute: 'sideways', reflect: true })
  sideways = false;

  /**
   * This defines the side position of the image for sideways variant style.
   */
  @property({ attribute: 'image-sideway-alignment' })
  imageSidewayAlignment: 'left' | 'right' = 'right';

  /**
   * Background color of the card. Defaults to 'white'
   */
  @property({ type: String, attribute: 'bg-color', reflect: true })
  bgColor: 'blue' | 'green' | 'white' | 'sky-blue' | 'none' = 'white';

  /**
   * Whether or not the component should be 100% height of its parent.
   * If TRUE, the Host will be `height: 100%`.
   */
  @property({ type: Boolean, attribute: 'height-grow', reflect: true })
  heightGrow = false;

  /**
   * CTA url.
   */
  @property({ type: String, attribute: 'cta-url', reflect: true })
  ctaUrl: string;

  /**
   * Secondary CTA url.
   */
  @property({ type: String, attribute: 'cta-url-secondary', reflect: true })
  ctaUrlSecondary: string;

  /**
   * CTA link caption.
   */
  @property({ type: String, attribute: 'cta-text', reflect: true })
  ctaText: string;

  /**
   * Secondary CTA link caption.
   */
  @property({ type: String, attribute: 'cta-text-secondary', reflect: true })
  ctaTextSecondary: string;

  /**
   * Use divider.
   */
  @property({ type: Boolean, attribute: 'use-divider', reflect: true })
  useDivider = false;

  @state() hasImgSlot: boolean;
  @state() hasEyebrowSlot: boolean;
  @state() hasHeadingSlot: boolean;
  @state() hasSubHeading: boolean;
  @state() hasContentSlot: boolean;
  @state() hasCtaSlot: boolean;
  @state() hasCtaSlotSecondary: boolean;
  @state() isHovered: boolean;

  /**
   * An accessible solution to making a entire component clickable if there's a link.
   */
  linkifyCards = () => {
    const ctaUrl = this.ctaUrl ?? this.ctaUrlSecondary ?? false;
    if (!ctaUrl || !this.isClickable) {
      return;
    }

    this.addEventListener('mouseenter', () => {
      this.isHovered = true;
    });

    this.addEventListener('mouseleave', () => {
      this.isHovered = false;
    });

    this.addEventListener('click', ev => {
      // Allow body links inside the card to bubble up normally.
      const targetElement = ev.composed ? ev.composedPath()[0] : null;
      const closestLink =
        targetElement instanceof Element && targetElement.closest('a');
      if (closestLink && closestLink.href !== ctaUrl) {
        return;
      }

      const headingSlot = this.querySelector('[slot="heading"]');
      const headingContent = (headingSlot?.textContent ?? '')
        .replace(/[\n\r]+/g, '')
        .trim();

      // We clicked on another part of the card or the CTA link.
      ev.preventDefault();
      ev.stopPropagation();
      window.open(ctaUrl, this.isClickableTarget, headingContent);
    });
  };

  firstUpdated(): void {
    super.firstUpdated();
    this.linkifyCards();

    if (this.site === 'BH' && this.variant === 'dark') {
      this.bgColor = 'blue';
    }

    this.hasImgSlot = this.querySelector('[slot="image"]') !== null;
    this.hasCtaSlot = this.querySelector('[slot="cta"]') !== null;

    if (this.variant === 'simple-link') {
      this.imageBleed = true;
      this.ctaUrlSecondary = '';
      this.hasEyebrowSlot = false;
      this.hasHeadingSlot = false;
      this.hasSubHeading = false;
      this.hasContentSlot = false;
      this.hasCtaSlotSecondary = false;
      return;
    }

    this.hasEyebrowSlot = this.querySelector('[slot="eyebrow"]') !== null;
    this.hasHeadingSlot = this.querySelector('[slot="heading"]') !== null;
    this.hasSubHeading = this.querySelector('[slot="subheading"]') !== null;
    this.hasContentSlot = this.querySelector('[slot="content"]') !== null;
    this.hasCtaSlotSecondary =
      this.querySelector('[slot="cta-secondary"]') !== null;
  }

  buttonVariant(secondaryCta: boolean) {
    if (this.variant === 'simple-link') {
      return 'primary';
    }
    if (this.site !== 'BH') {
      return 'cta-dark';
    }
    if (this.bgColor === 'blue' || this.imageBleed) {
      if (secondaryCta) {
        return 'cta-light';
      }
      return 'tertiary';
    }
    if (secondaryCta) {
      return 'cta-dark';
    }
    return 'primary';
  }

  buttonIcon() {
    if (this.isClickableTarget === '_blank') {
      return 'arrow-up-right-from-square-solid';
    }

    if (this.site === 'BH') {
      return 'chevron-right';
    }

    return 'chevron-circle';
  }

  render(): TemplateResult {
    const wrapperClasses = {
      'card-wrapper': true,
      [`card-wrapper-${this.bgColor}`]: true,
      [`card-wrapper-${this.variant}`]: true,
      'is-hovered': this.isHovered,
      'text-white':
        this.bgColor === 'blue' || this.bgColor === 'green' || this.imageBleed,
      'image-sideway': this.imageSideway,
      [`image-sideway--${this.imageSidewayAlignment}`]:
        this.imageSideway && this.imageSidewayAlignment,
      'sideways': this.sideways,
      'card--image-bleed': this.imageBleed,
    };

    return html`
      <div class="${classMap(wrapperClasses)}">
        ${this.imageBleed
          ? html`<div class="overlay op-${this.overlayOpacity}"></div>`
          : nothing}
        <div class="card">
          ${this.hasImgSlot
            ? html`<div class="card-image">
                <slot name="image"></slot>
                ${this.variant === 'simple-link'
                  ? html` <div class="inner-border"></div> `
                  : nothing}
              </div> `
            : nothing}
          <div class="card-body-wrapper">
            ${this.hasEyebrowSlot
              ? html`<div class="card-eyebrow">
                  <slot name="eyebrow"></slot>
                </div>`
              : nothing}
            ${this.hasHeadingSlot
              ? html`
                  ${this.isClickable && this.ctaUrl
                    ? html`
                        <a
                          class="card-heading"
                          href="${this.ctaUrl}"
                          ${this.isClickableTarget
                            ? html`target="${this.isClickableTarget}"
                              rel="noreferrer noopener"`
                            : nothing}
                        >
                          <slot name="heading"></slot>
                        </a>
                      `
                    : html`
                        <div class="card-heading">
                          <slot name="heading"></slot>
                        </div>
                      `}
                `
              : ''}
            ${this.hasSubHeading
              ? html`
                  <div class="card-subheading">
                    ${this.iconName
                      ? html`
                          <ghs-icon
                            name="${this.iconName}"
                            size="16px"
                            render-for-site="BH"
                          >
                          </ghs-icon>
                        `
                      : nothing}
                    <slot name="subheading"></slot>
                  </div>
                `
              : ''}
            ${this.hasContentSlot
              ? html`
                  <div class="card-content">
                    <slot name="content"></slot>
                  </div>
                `
              : ''}
            ${this.ctaUrl ||
            this.ctaUrlSecondary ||
            this.hasCtaSlot ||
            this.hasCtaSlotSecondary
              ? html`
                  <div
                    class="${classMap({
                      'card-cta': true,
                      'card-cta--divider': this.useDivider,
                    })}"
                  >
                    ${this.ctaUrl || this.hasCtaSlot
                      ? html`
                          <slot name="cta">
                            ${this.ctaUrl
                              ? html`
                                  <ghs-button
                                    url="${this.ctaUrl}"
                                    variant="${this.buttonVariant(false)}"
                                    icon-position="right"
                                    is-hovered=${this.isHovered}
                                    icon="${this.buttonIcon()}"
                                    button-target="${this.isClickableTarget}"
                                    size="medium"
                                    icon-size="12"
                                    site=${this.site}
                                  >
                                    ${this.ctaText}
                                  </ghs-button>
                                `
                              : nothing}
                          </slot>
                        `
                      : nothing}
                    ${this.site === 'BH' &&
                    (this.ctaUrlSecondary || this.hasCtaSlotSecondary)
                      ? html`
                          <div class="cta-secondary">
                            <slot name="cta-secondary">
                              ${this.ctaUrlSecondary
                                ? html`
                                    <ghs-button
                                      url="${this.ctaUrlSecondary}"
                                      variant="${this.buttonVariant(true)}"
                                      icon-position="right"
                                      icon="${this.isClickableTarget == '_blank'
                                        ? 'arrow-up-right-from-square-solid'
                                        : 'chevron-right'}"
                                      button-target="${this.isClickableTarget}"
                                      size="medium"
                                      icon-size="12"
                                      site=${this.site}
                                    >
                                      ${this.ctaTextSecondary}
                                    </ghs-button>
                                  `
                                : nothing}
                            </slot>
                          </div>
                        `
                      : nothing}
                  </div>
                `
              : nothing}
          </div>
        </div>
      </div>
    `;
  }
}
